import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Canonical Badge component — Academy Pro design system.
 *
 * Base: rounded-full (pill), px-2.5 py-0.5, text-xs font-bold, border, inline-flex.
 * Tap target: Badge is display-only; interactive wrappers must be ≥ 44×44 px.
 *
 * Semantic variants:
 *   default     — brand-primary blue  (general label, pending status)
 *   secondary   — gray neutral        (inactive, archived)
 *   success     — emerald             (positive result, approved, high score)
 *   warning     — amber               (reserve, caution, mid score)
 *   danger      — rose                (negative result, low score, critical)
 *   outline     — no fill, border     (neutral detail)
 *
 * Score format rules (CANONICAL):
 *   - Total scores (CCRI, ARS): display as "{value} / 100"  e.g. "CCRI 84 / 100"
 *   - Sub-criterion scores:     display as "{value} / {max}" e.g. "15 / 20"
 *   - CV career score:          display as "{value} / 10"   e.g. "8 / 10"
 *   - NEVER use "/50" for totals — legacy v1/v2 /50 max is superseded by v3 /100
 *   - Space around slash: "84 / 100" NOT "84/100"
 *
 * Usage:
 *   <Badge variant="success">CCRI 84 / 100</Badge>
 *   <Badge variant="warning">Beklemede</Badge>
 *   <Badge variant="danger">Yetersiz</Badge>
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // General / shadcn compat
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline:
          "border-gray-300 text-gray-700 bg-transparent",
        // Semantic — interview module & shared
        success:
          "border-emerald-200 bg-emerald-50 text-emerald-700",
        warning:
          "border-amber-200 bg-amber-50 text-amber-700",
        danger:
          "border-rose-200 bg-rose-50 text-rose-700",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  ...props
}) {
  return (<div className={cn(badgeVariants({ variant }), className)} {...props} />);
}

export { Badge, badgeVariants }
